/**
 * Shopify Wishlist — Account-synced via Cloudflare Worker + Customer Metafields
 *
 * - Logged-in customers: wishlist saved to their Shopify account (syncs across devices)
 * - Guest visitors: wishlist saved to localStorage (lost on logout, prompts to login)
 *
 * Requires: window.__wishlist injected by theme.liquid (see theme-snippet.liquid)
 */

const Wishlist = (() => {

  const API_URL         = '/apps/wishlist';   // Shopify App Proxy URL
  const STORAGE_KEY     = 'shopify_wishlist'; // Fallback for guests
  const LOGIN_URL       = '/account/login?return_url=/pages/wishlist';
  const MAX_ITEMS       = 50;                 // Max wishlist items per customer

  let _items     = [];
  let _loggedIn  = false;
  let _saving    = false;

  // ── Init ────────────────────────────────────────────────────────────────────
  async function init() {
    const meta = window.__wishlist;

    if (meta && meta.customerId) {
      // Logged-in customer — load from metafield
      _loggedIn = true;
      try {
        const res  = await fetch(API_URL);
        const data = await res.json();
        _items = Array.isArray(data.items) ? data.items : [];
      } catch (err) {
        console.error('Wishlist: failed to load from account', err);
        _items = [];
      }
    } else {
      // Guest — load from localStorage
      _loggedIn = false;
      _items    = _loadLocal();
    }

    _bindButtons();
    syncButtons();
    syncCount();
    renderPage();
  }

  // ── Core ────────────────────────────────────────────────────────────────────
  function has(productId) {
    return _items.some(item => item.id === productId);
  }

  function count() {
    return _items.length;
  }

  function getAll() {
    return _items;
  }

  async function add(product) {
    if (has(product.id)) return;

    if (_items.length >= MAX_ITEMS) {
      _showNotice(`Your wishlist is full! You can save up to ${MAX_ITEMS} items.`);
      return;
    }

    _items.push(product);
    await _persist();
  }

  async function remove(productId) {
    _items = _items.filter(item => item.id !== productId);
    await _persist();
  }

  async function toggle(product) {
    has(product.id) ? await remove(product.id) : await add(product);
  }

  // ── Persist ─────────────────────────────────────────────────────────────────
  async function _persist() {
    if (_loggedIn) {
      await _saveToAccount();
    } else {
      _saveLocal();
    }
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: _items } }));
  }

  async function _saveToAccount() {
    if (_saving) return;
    _saving = true;
    try {
      await fetch(API_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items: _items }),
      });
    } catch (err) {
      console.error('Wishlist: failed to save to account', err);
    } finally {
      _saving = false;
    }
  }

  function _saveLocal() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(_items));
    } catch (err) {
      console.error('Wishlist: localStorage save failed', err);
    }
  }

  function _loadLocal() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  // ── Button wiring ────────────────────────────────────────────────────────────
  function _bindButtons() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;

      // Guest — redirect to login
      if (!_loggedIn) {
        window.location.href = LOGIN_URL;
        return;
      }

      const product = {
        id:     parseInt(btn.dataset.productId),
        title:  btn.dataset.productTitle,
        handle: btn.dataset.productHandle,
        price:  btn.dataset.productPrice,
        image:  btn.dataset.productImage,
        url:    btn.dataset.productUrl,
      };

      btn.disabled = true;
      await toggle(product);
      btn.disabled = false;

      syncButtons();
      syncCount();

      // Pop animation
      btn.classList.add('wl-pop');
      btn.addEventListener('animationend', () => btn.classList.remove('wl-pop'), { once: true });
    });
  }

  // ── UI sync ──────────────────────────────────────────────────────────────────

  /**
   * Updates all heart buttons to reflect current wishlist state.
   */
  function syncButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const id     = parseInt(btn.dataset.productId);
      const active = has(id);
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-label',  active ? 'Remove from wishlist' : 'Add to wishlist');
      btn.setAttribute('aria-pressed', active);
    });
  }

  /**
   * Updates wishlist count badges in the header.
   */
  function syncCount() {
    const c = count();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent  = c;
      el.style.display = c > 0 ? '' : 'none';
    });
  }

  /**
   * Renders the wishlist page grid.
   */
  function renderPage() {
    const container = document.querySelector('[data-wishlist-page]');
    if (!container) return;

    const empty = document.querySelector('[data-wishlist-empty]');
    const login = document.querySelector('[data-wishlist-login]');
    const grid  = document.querySelector('[data-wishlist-grid]');

    // Show login prompt for guests
    if (!_loggedIn) {
      if (empty) empty.style.display = 'none';
      if (login) login.style.display = '';
      if (grid)  grid.innerHTML = '';
      return;
    }

    if (login) login.style.display = 'none';

    const items = getAll();

    if (items.length === 0) {
      if (empty) empty.style.display = '';
      if (grid)  grid.innerHTML = '';
      return;
    }

    if (empty) empty.style.display = 'none';

    if (grid) {
      grid.innerHTML = items.map(item => `
        <div class="wl-card" data-wishlist-card="${item.id}">
          <a href="${item.url}" class="wl-card__image-link">
            <img
              src="${item.image}"
              alt="${item.title}"
              class="wl-card__image"
              loading="lazy"
              width="400"
              height="400"
            />
          </a>
          <div class="wl-card__info">
            <a href="${item.url}" class="wl-card__title">${item.title}</a>
            <p class="wl-card__price">${item.price}</p>
          </div>
          <div class="wl-card__actions">
            <a href="${item.url}" class="wl-card__btn wl-card__btn--view">View Product</a>
            <button
              class="wl-card__btn wl-card__btn--remove"
              data-remove-id="${item.id}"
              aria-label="Remove ${item.title} from wishlist"
            >
              Remove
            </button>
          </div>
        </div>
      `).join('');

      grid.querySelectorAll('[data-remove-id]').forEach(btn => {
        btn.addEventListener('click', async () => {
          await remove(parseInt(btn.dataset.removeId));
          renderPage();
          syncCount();
        });
      });
    }
  }

  // ── Toast notice ─────────────────────────────────────────────────────────────
  function _showNotice(message) {
    // Remove any existing notice
    const existing = document.querySelector('.wl-notice');
    if (existing) existing.remove();

    const el = document.createElement('div');
    el.className   = 'wl-notice';
    el.textContent = message;
    document.body.appendChild(el);

    // Trigger animation
    requestAnimationFrame(() => el.classList.add('wl-notice--visible'));

    // Auto remove after 3.5s
    setTimeout(() => {
      el.classList.remove('wl-notice--visible');
      el.addEventListener('transitionend', () => el.remove(), { once: true });
    }, 3500);
  }

  return { init, has, add, remove, toggle, count, getAll, syncButtons, syncCount, renderPage };
})();

document.addEventListener('DOMContentLoaded', () => Wishlist.init());